// assets
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import QapiHeader from "./templates/qapi_header"
import QapiBody from "./templates/qapi_body"
import ReportsHeader from "../components/Reports_template/Reports_header";
import ReportsTemplate from "../components/Reports_template/Reports_template";
import {
  PrintOnlyContainer,
  PrintableImage,
  ReportsContainer,
} from "./qapi.styles";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  generateBodyObject,
  generateHeaderObject,
} from "../utils/helpers/incidentReport";
import { useReactToPrint } from "react-to-print";
import { tabSeparatedData } from "../utils/formGenUtils/getTabSeparatedData";
import useIncidentTypes from "../hooks/useIncidentTypes";
import useSelectOptions from "../hooks/useSelectOptions";
import useIncident from "../hooks/useIncident";
import CustomLoader from "../components/CustomLoader";
import { getInitialValues } from "../utils/formGenUtils/getInitialValues";
import { IFieldInstance } from "../types/fieldInstance.interface";
import { transformArrayValues, populateInlineRLs } from "../utils/formGenUtils/transformArrayValues";
import { usePermission } from "../hooks/usePermission";
import { PERMISSION_LIST } from "../shared/sharedConstants";
import { prependMimeType, resetViewConfig } from "../utils/helpers";
import PdfPrintViewer from "../screens/Incidents/attachments/PdfPrintViewer";
import DocxViewer from "../screens/Incidents/attachments/DocxViewer";
import { useMultipleAttachments } from "../../src/hooks/useAttachments";
import {
  AttachmentLineItem,
  FileViewModal,
} from "../../src/screens/Incidents/attachments/AttachmentExports";

const QapiReport = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { id } = useParams();
  const params = new URLSearchParams(search);
  const { incidentTypes, incidentTypesLoading } = useIncidentTypes();
  const containerRef = useRef() as any;

  const { isPermitted } = usePermission();

  const [showDownloadBtn, setShowDownloadBtn] = useState(false);
  const [showAttachmentPrint, setShowAttachmentPrint] = useState(false);
  const [attachmentId, setAttachmentId] = useState("");
  const [fileViewConfig, setFileViewConfig] = useState(resetViewConfig());

  const incidentId = id ?? "";

  const {
    getIncident,
    getIncidentSignatories,
    handleGetIncidentAttachments,
    handleGetSingleAttachments,
  } = useIncident({ incidentId, attachmentId });

  const {
    data: incidentData,
    isLoading: incidentDataLoading,
    isError: incidentDataError,
  } = getIncident;

  const { data: _incidentAttachments, isLoading: attsLoading } =
    handleGetIncidentAttachments;

  const { data: _incidentAttachment, isLoading: isLoadingAttachment } =
    handleGetSingleAttachments;

  const incidentAttachments = _incidentAttachments?.data?.data || [];

  const { data: attachmentsWithData, isLoading: isLoadingAttachments } =
    useMultipleAttachments(incidentAttachments);

  useEffect(() => {
    if (_incidentAttachment !== undefined) {
      setFileViewConfig({
        ...fileViewConfig,
        viewFile: true,
        file: prependMimeType(
          _incidentAttachment?.data?.data,
          fileViewConfig.fileExtension
        ),
      });
    }
  }, [_incidentAttachment]);

  const incident =
    incidentDataLoading || incidentDataError ? null : incidentData?.data?.data;

  useEffect(() => {
    if (!incidentId) {
      navigate("/user/incidents-list");
    }
  }, []);

  useEffect(() => {
    setShowDownloadBtn(
      isPermitted(PERMISSION_LIST.EXPORT_REPORT_AS_CSV, incident?.Facility__c)
    );
  }, [incident]);

  const initialValues = useMemo(
    () =>
      getInitialValues(
        Object.values(tabSeparatedData).flat() as IFieldInstance[]
      ),
    [tabSeparatedData]
  );

  const _baseTransformed = incident
    ? {
        ...initialValues,
        ...incident,
        ...transformArrayValues(initialValues, incident),
      }
    : { ...initialValues };

  const transformedIncident = {
    ..._baseTransformed,
    ...populateInlineRLs(_baseTransformed, initialValues),
  };

  const {
    isLoading: signatoriesLoading,
    data: signatoriesData,
    isError: signatoriesDataError,
  } = getIncidentSignatories;
  const signatories =
    signatoriesLoading || signatoriesDataError
      ? []
      : signatoriesData?.data.data;

  const facility = incident?.Facility__r?.Id || "";

  const selectOptions = useSelectOptions(facility);

  const { allLookUpOptions, errorGettingOptions, optionsLoading } =
    selectOptions;

  const _allLookUpOptions = useMemo(() => {
    return allLookUpOptions ? Object.values(allLookUpOptions).flat() : [];
  }, [allLookUpOptions, errorGettingOptions, optionsLoading]);
  const reportVersion = params.get("version");
  const includeStatements = params.get("includeStatements");
  const includeConclusion = params.get("includeConclusion");
  const includeAttachments = params.get("includeAttachments");

  const isNonQapi = !reportVersion;

  const headerData = useMemo(() => {
    return generateHeaderObject(
      transformedIncident,
      incident?.Incident_Name__c
    );
  }, [
    signatories,
    signatoriesLoading,
    signatoriesDataError,
    transformedIncident,
  ]);

  // Set page title to incident number so the print/save dialog defaults to it
  useEffect(() => {
    if (headerData.incidentNumber) {
      document.title = headerData.incidentNumber;
    }
    return () => {
      document.title = 'Mitigate Risk Web Portal';
    };
  }, [headerData.incidentNumber]);

  const transformedData = useCallback(() => {
    return generateBodyObject(
      transformedIncident,
      incidentTypes,
      _allLookUpOptions,
      tabSeparatedData,
      signatories,
      isNonQapi,
      !!includeStatements,
      !!includeConclusion
    );
  }, [
    incidentTypes,
    signatories,
    _allLookUpOptions,
    errorGettingOptions,
    optionsLoading,
    transformedIncident,
  ]);

  // react-to-print clones the DOM into a hidden iframe, but <canvas> elements
  // clone *empty* (their bitmap doesn't transfer). react-pdf renders PDF pages
  // to canvases, so without this swap the PDF attachments print as blank pages.
  // We snapshot each canvas to an <img> via toDataURL just before print and
  // hide the originals; everything is restored after print.
  const swapCanvasesForPrint = (root: HTMLElement) => {
    const canvases = Array.from(
      root.querySelectorAll<HTMLCanvasElement>("canvas")
    );
    canvases.forEach((canvas) => {
      try {
        const img = document.createElement("img");
        img.src = canvas.toDataURL("image/png");
        img.style.width = `${canvas.clientWidth || canvas.width}px`;
        img.style.height = "auto";
        img.style.display = "block";
        img.dataset.canvasPrintSwap = "1";
        canvas.style.display = "none";
        canvas.dataset.canvasPrintHidden = "1";
        canvas.parentElement?.insertBefore(img, canvas);
      } catch {
        /* tainted canvas or other failure — leave canvas as-is */
      }
    });
  };

  const restoreCanvasesAfterPrint = (root: HTMLElement) => {
    root
      .querySelectorAll<HTMLImageElement>("img[data-canvas-print-swap='1']")
      .forEach((img) => img.remove());
    root
      .querySelectorAll<HTMLCanvasElement>("canvas[data-canvas-print-hidden='1']")
      .forEach((canvas) => {
        canvas.style.display = "";
        delete canvas.dataset.canvasPrintHidden;
      });
  };

  const handlePrint = useReactToPrint({
    content: () => containerRef.current,
    documentTitle: headerData.incidentNumber || 'Gener8 Risk Reporter',
    pageStyle: `
      @page {
        margin: 1cm 1cm 1.5cm 1cm;
        @bottom-right {
          content: "Page " counter(page);
          font-size: 9pt;
          color: #666;
        }
      }
      @media print {
        body {
          padding-bottom: 80px;
        }
      }
    `,
    onBeforeGetContent: () => {
      // Wait briefly so any in-flight canvas renders settle, then snapshot
      // every canvas into a same-sized <img> so it survives DOM cloning.
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          if (containerRef.current) {
            swapCanvasesForPrint(containerRef.current);
          }
          resolve();
        }, 600);
      });
    },
    onBeforePrint: () => setShowAttachmentPrint(true),
    onAfterPrint: () => {
      if (containerRef.current) {
        restoreCanvasesAfterPrint(containerRef.current);
      }
      setShowAttachmentPrint(false);
    },
  });

  const showLoader =
    signatoriesLoading ||
    incidentDataLoading ||
    incidentTypesLoading ||
    (optionsLoading && !errorGettingOptions);

  if (showLoader) {
    return (
      <div>
        <CustomLoader fullscreen />
      </div>
    );
  }

  if (incidentDataError || signatoriesDataError) {
    return <>Error Occurred. Please refresh.</>;
  }

  return (
    <ReportsContainer className="report_container">
      {showDownloadBtn && <button onClick={handlePrint}>Download PDF</button>}
      <div ref={containerRef} style={{ width: "100%" }}>
        <QapiHeader reportHeading={headerData} isNonQapi={isNonQapi} />
        <QapiBody incident={incident} transformedIncident={transformedData()} isNonQapi={isNonQapi} includeStatements={!!includeStatements} />
        <PrintOnlyContainer>
          {!showAttachmentPrint &&
            includeAttachments &&
            !isLoadingAttachments &&
            attachmentsWithData.map((file: any) => (
              <Fragment key={file?.attachmentId}>
                {["png", "jpg", "jpeg"].includes(file.fileExtension) && (
                  <PrintableImage
                    src={prependMimeType(file.base64Data, file.fileExtension)}
                    alt={`Attachment ${file?.attachmentId}`}
                  />
                )}
                {file.fileExtension === "pdf" && (
                  <PdfPrintViewer
                    fileSrc={prependMimeType(
                      file.base64Data,
                      file.fileExtension
                    )}
                  />
                )}
                {file.fileExtension === "docx" && (
                  <DocxViewer
                    fileSrc={prependMimeType(
                      file.base64Data,
                      file.fileExtension
                    )}
                    print
                  />
                )}
              </Fragment>
            ))}
        </PrintOnlyContainer>
      </div>
      {includeAttachments && (
        <footer className="mb-2 mt-2">
          {isLoadingAttachments ? (
            <CustomLoader />
          ) : (
            <div className="flex flex-wrap gap-6">
              {attachmentsWithData.map((attachment: any) => {
                // Transform the attachment to have 'data' property instead of 'base64Data'
                // so it works with AttachmentLineItem component
                const transformedAttachment = {
                  ...attachment,
                  data: attachment.base64Data,
                };
                return (
                  <AttachmentLineItem
                    key={attachment?.attachmentId}
                    attachment={transformedAttachment}
                    setAttachmentId={setAttachmentId}
                    setFileViewConfig={setFileViewConfig}
                    fileViewConfig={fileViewConfig}
                    setDeleteConfig={setFileViewConfig}
                    showDelete={false}
                  />
                );
              })}
            </div>
          )}
          {fileViewConfig.viewFile && (
            <FileViewModal
              fileViewConfig={fileViewConfig}
              setFileViewConfig={setFileViewConfig}
              isLoadingAttachment={isLoadingAttachment}
              setAttachmentId={setAttachmentId}
            />
          )}
        </footer>
      )}
    </ReportsContainer>
  );
};

export default QapiReport;
